import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useFormik, yupToFormErrors, validateYupSchema } from 'formik';

import { CSSObjectWithLabel } from 'react-select';

import ModalFormDatepicker from '@/components/business/ModalForm/ModalFormDatepicker/ModalFormDatepicker';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import ModalFormTextarea from '@/components/business/ModalForm/ModalFormTextarea/ModalFormTextarea';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import { DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { addDays, getDateWithProperTimeOffset, isValidDate } from '@/utils/date.utils';
import { TerminateContractLinesFormFields, validationSchema } from './validationSchema';
import { isTerminateDateOutOfRenewalRange } from './utils';
import { CANCEL_TYPE } from '@/config/reasonTypes.config';
import { TerminateContractLinesDto } from '../hooks/useTerminateContractLines';
import ReadonlyField from '@/components/common/ModalWindow/ReadonlyField/ReadonlyField';
import LinesCount from '@/components/common/ModalWindow/LinesCount/LinesCount';

import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';

interface ITerminateContractReasonModalProps {
  contract: Contract | undefined;
  onClose: () => void;
  terminateContractLines: (params: TerminateContractLinesDto) => void;
  selectedContractLinesCount: number;
  reasonCodes: ReasonCode[];
  reasonTypes: ReasonType[];
  isLoadingReasonCodes?: boolean;
  lastBillingDate?: string | null;
  readOnly?: boolean;
}

export default function TerminateContractReasonModal({
  contract,
  terminateContractLines,
  onClose,
  selectedContractLinesCount,
  reasonCodes,
  reasonTypes,
  isLoadingReasonCodes,
  lastBillingDate,
  readOnly
}: ITerminateContractReasonModalProps) {
  const [isConfirmWindowVisible, setIsConfirmWindowVisible] = useState<boolean>(false);
  const [isTerminateWarningWindowVisible, setIsTerminateWarningWindowVisible] = useState<boolean>(false);
  const [isBillingDateWarningVisible, setIsBillingDateWarningVisible] = useState<boolean>(false);

  const initialValues = useMemo(() => ({
    reasonCode: '',
    comment: '',
    terminationDate: getDateWithProperTimeOffset(
      contract?.contractEndDate ? addDays(new Date(contract?.contractEndDate), 1) : new Date()
    ),
  }), [contract]);

  const validate = async (formValues: TerminateContractLinesFormFields) => {
    const context = {
      contractEndDate: contract?.contractEndDate,
      contractRenewableTerm: contract?.renewableTerm
    };

    try {
      await validateYupSchema(formValues, validationSchema, true, context);
    }
    catch (err) {
      return yupToFormErrors(err);
    }

    return {};
  };

  const { values, setFieldValue, errors, isValid, setFieldTouched, touched } = 
    useFormik<TerminateContractLinesFormFields>({
      initialValues,
      validationSchema,
      validate,
      onSubmit: () => undefined,
      validateOnMount: true,
    });

  const confirmTerminate = () => {
    if (lastBillingDate && values.terminationDate && dayjs(values.terminationDate).isBefore(dayjs(lastBillingDate))) {
      setIsBillingDateWarningVisible(true);
      return;
    }

    if (isTerminateDateOutOfRenewalRange(values.terminationDate, contract?.contractEndDate)) {
      setIsTerminateWarningWindowVisible(true);
    } else {
      setIsConfirmWindowVisible(true);
    }
  };

  const onConfirmBillingDateWarning = () => {
    setIsBillingDateWarningVisible(false);
    if (isTerminateDateOutOfRenewalRange(values.terminationDate, contract?.contractEndDate)) {
      setIsTerminateWarningWindowVisible(true);
    } else {
      setIsConfirmWindowVisible(true);
    }
  };

  const onCloseTerminateWarningWindow = () => {
    setIsTerminateWarningWindowVisible(false);
  };

  const onConfirm = () => {
    terminateContractLines(values);
    onClose();
  };

  return (
    <>
      {isConfirmWindowVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="Confirm line termination"
          onClose={onClose}
          onConfirm={onConfirm}
          contentStyle={{ width: 600 }}
        >
          {/* eslint-disable-next-line max-len */}
          You are about to terminate the line(s) with the Termination Date of {dayjs(values.terminationDate).format(DEFAULT_DATE_PATTERN)}. Are you sure you want to proceed?
        </AgConfirmationModal>
      )}

      {isTerminateWarningWindowVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="You are about to set Termination date that is out of Contract Renewal window"
          onClose={onCloseTerminateWarningWindow}
          onConfirm={onConfirm}
          contentStyle={{ width: 680 }}
        >
          The Termination Date set for the Contract/Contract Lines are out of range of Contract Renewal.
          <br />
          Please check the Termination Date and confirm the selection.
        </AgConfirmationModal>
      )}

      {isBillingDateWarningVisible && (
        <AgConfirmationModal
          isOpen={true}
          title="Termination date is before the last billing date"
          onClose={() => setIsBillingDateWarningVisible(false)}
          onConfirm={onConfirmBillingDateWarning}
          contentStyle={{ width: 680 }}
        >
          Termination date is before the last billing end date. Please review and make a credit note request.
        </AgConfirmationModal>
      )}

      <AgModalWindow
        title="Terminate Reason"
        isOpen={true}
        onClose={onClose}
        contentStyle={{ width: 340 }}
      >
        {readOnly && (
          <div
            style={{
              padding: '10px 15px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#856404',
              marginBottom: '15px',
            }}
          >
            <strong>Note: </strong>Only users with Administrator or Full Access roles can edit information.
          </div>
        )}

        <LinesCount className="mb-10">
          {`${selectedContractLinesCount} Lines Selected`}
        </LinesCount>
        <div className="mb-15">
          <ModalFormDropdown<ReasonType>
            id="terminateReasonType"
            labelTitle="Reason Type"
            optionsData={reasonTypes}
            getOptionLabel={(option) => option.formattedType}
            getOptionValue={(option) => option.type}
            disabled
            defaultValue={reasonTypes.find((el) => el.type === CANCEL_TYPE)}
          />
        </div>
        <div className="mb-15">
          <ModalFormDropdown<ReasonCode>
            id="terminateReason"
            labelTitle="Reason Name"
            requiredIcon={true}
            isSearchable={true}
            optionsData={reasonCodes}
            placeholder="Select or type to search"
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.code}
            selectedOption={reasonCodes.find((el) => el.code === values.reasonCode) ?? null}
            customStyles={{
              menuList: (provided) => ({ ...provided, maxHeight: 200 } as CSSObjectWithLabel)
            }}
            menuPortalTarget={document.querySelector('body')}
            handleChange={(newValue) => {
              setFieldTouched('reasonCode', true, false);
              setFieldValue('reasonCode', newValue?.code);
            }}
            validationMessage={
              (errors?.reasonCode && touched.reasonCode) ? errors.reasonCode : undefined
            }
            isLoading={isLoadingReasonCodes}
            disabled={isLoadingReasonCodes}
          />
        </div>

        <div className="mb-15">
          <ModalFormDatepicker
            id="terminationDate"
            labelTitle="Termination Date"
            requiredIcon={true}
            value={isValidDate(values.terminationDate) ? new Date(values.terminationDate) : undefined}
            onChange={({ value }) => {
              setFieldTouched('terminationDate', true, false);
              setFieldValue('terminationDate', value);
            }}
            validationMessage={
              (errors?.terminationDate && touched.terminationDate) ? errors.terminationDate as string : undefined
            }
          />
        </div>

        <div className="mb-15">
          <ModalFormTextarea
            id="terminateComment"
            labelTitle="Comment"
            requiredIcon={true}
            placeholder="Enter short comments"
            onChange={(event) => {
              setFieldTouched('comment', true, false);
              setFieldValue('comment', event.target.value);
            }}
            validationMessage={
              (errors?.comment && touched.comment) ? errors.comment : undefined
            }
          />
        </div>

        <div className="mb-15">
          <TopLabel label="Contract End Date" />
          <ReadonlyField>
            {contract?.contractEndDate
              ? dayjs(contract.contractEndDate).format(DEFAULT_DATE_PATTERN)
              : 'Not specified'}
          </ReadonlyField>
        </div>

        <div className="mb-15">
          <TopLabel label="Contract Term (Months)" />
          <ReadonlyField>{contract?.term}</ReadonlyField>
        </div>


        <ActionButtonsWrapper
          leftButton={(
            <Button
              buttonStyleType={ButtonStyleType.SECONDARY}
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
          rightButton={(
            <Button
              disabled={!isValid || readOnly}
              buttonStyleType={ButtonStyleType.PRIMARY}
              onClick={confirmTerminate}
            >
              Save
            </Button>
          )}
        />
      </AgModalWindow>
    </>
  );
}
